import { trackActionTrigger } from '../support/actionTriggerGuard';

export default () => ({
    // Unified reentrancy flag. Guards ALL paths of execute()
    // (dispatch, navigation, ActionDialog) – not just navigation.
    _busy: false,

    _pageShowRef: null,
    _pageHideRef: null,
    _busyGeneration: 0,

    init() {
        // bfcache restore (Back/Forward): reset the busy flag so the handler
        // does not stay stuck in a busy state when the user returns
        // to the page without a full reload.
        this._pageShowRef = (event) => {
            if (event.persisted) {
                this._clearBusy();
            }
        };
        // Regular page unloads / soft navigations: also release the guard
        // so a potential second handler instance (per-page) starts clean.
        this._pageHideRef = () => {
            this._clearBusy();
        };

        window.addEventListener('pageshow', this._pageShowRef);
        window.addEventListener('pagehide', this._pageHideRef);
        document.addEventListener('livewire:navigating', this._pageHideRef);
        document.addEventListener('livewire:navigated', this._pageHideRef);
    },

    destroy() {
        if (this._pageShowRef) {
            window.removeEventListener('pageshow', this._pageShowRef);
            this._pageShowRef = null;
        }
        if (this._pageHideRef) {
            window.removeEventListener('pagehide', this._pageHideRef);
            document.removeEventListener('livewire:navigating', this._pageHideRef);
            document.removeEventListener('livewire:navigated', this._pageHideRef);
            this._pageHideRef = null;
        }
        this._clearBusy();
    },

    _clearBusy() {
        this._busy = false;
        this._busyGeneration++;
    },

    _dispatchTracked(name, payload) {
        this._busy = true;
        const generation = ++this._busyGeneration;
        trackActionTrigger(globalThis.Livewire, () => this.$dispatch(name, payload), () => {
            if (this._busyGeneration === generation) this._clearBusy();
        });
    },

    _navigate(callback) {
        this._busy = true;
        const generation = ++this._busyGeneration;
        try {
            callback();
        } finally {
            // Browser links/Livewire.navigate expose no completion promise.
            // Collapse synchronous repeats without trapping cancelled/failed links.
            queueMicrotask(() => {
                if (this._busyGeneration === generation) this._clearBusy();
            });
        }
    },

    execute(data, event = null) {
        if (data.disabled) {
            event?.preventDefault();
            return;
        }

        if (event) {
            const isMiddleClick = event.button === 1;
            const isMetaClick = event.ctrlKey || event.metaKey;
            const isSimpleRoute = !!data.route && !data.confirm;

            if (isSimpleRoute && (isMiddleClick || isMetaClick)) {
                window.open(data.route.url, '_blank', 'noopener,noreferrer');
                event.preventDefault();
                return;
            }

            // Always prevent default for non-link actions or if it's a normal click on a route
            event.preventDefault();
        }

        if (data.modelIds === '__SELECTED__') {
            data.modelIds = this.selected || [];
        }

        if (data.route?.url?.includes('__SELECTED__') && Array.isArray(data.modelIds)) {
            if (data.modelIds.length > 0) {
                data.route.url = data.route.url.replace('__SELECTED__', data.modelIds[0]);
            }
        }

        // Opening a new tab does NOT leave the current page – it must stay
        // exempt from the reentrancy guard so the user can open multiple
        // tabs in a row.
        const isBlankLink = !data.dispatch
            && !!data.route
            && !data.confirm
            && data.route.target === '_blank';

        if (!isBlankLink && this._busy) {
            // A previous execute() is still "in flight": navigation pending,
            // dispatch just fired, or an ActionDialog was just requested.
            // Swallow the duplicate so impatient multi-clicks do not cause
            // double dispatch / double dialog / double navigation.
            return;
        }

        if (data.dispatch) {
            this._dispatchTracked(data.dispatch.event, data.dispatch.payload ?? {});
            return;
        }

        // Simple link action: no confirmation, no fields
        if (data.route && !data.confirm) {
            if (isBlankLink) {
                window.open(data.route.url, data.route.target, 'noopener,noreferrer');
                return;
            }
            if (data.route.navigate) {
                this._navigate(() => Livewire.navigate(data.route.url));
                return;
            }
            this._navigate(() => { window.location.href = data.route.url; });
            return;
        }

        // Complex action: needs dialog/confirmation/fields.
        let dialogData = data;
        if (
            Object.prototype.hasOwnProperty.call(data, 'disabled')
            || Object.prototype.hasOwnProperty.call(data, 'disabledReason')
        ) {
            dialogData = { ...data };
            delete dialogData.disabled;
            delete dialogData.disabledReason;
        }
        this._dispatchTracked('showActionDialog', dialogData);
    },
});
